import {
  type FooterAdditionalInformationSectionConfig,
  type FooterConfig,
  type FooterContactSectionConfig,
  type FooterCopyrightAndSocialsSectionConfig,
  type FooterNavigationLinksSectionConfig,
  type FooterUnderageSectionConfig,
} from '../../footer.types.ts';
import { ROUTES } from './routes/routes.ts';

const contactSection: FooterContactSectionConfig = {
  items: [
    {
      type: 'chat',
      labelKey: 'lopetto_button',
      trackName: 'chat',
    },
    {
      type: 'link',
      href: ROUTES.HELP_CENTRE.BASE_URL,
      iconKey: 'Help',
      labelKey: 'help_button',
      trackName: 'Help Centre',
    },
  ],
};

const navigationLinks: FooterNavigationLinksSectionConfig = {
  groups: [
    {
      titleKey: 'lottery',
      trackName: 'Lottery',
      links: [
        {
          href: ROUTES.FOOTER.NAVIGATION.LOTTERY.RESULTS,
          labelKey: 'lottery_results',
          trackName: 'Lottery Results',
        },
        {
          href: ROUTES.LOTTO.CATEGORIES.ALL,
          labelKey: 'jackpots',
          trackName: 'Jackpots',
        },
        {
          href: ROUTES.LOTTO.OFFERS,
          labelKey: 'offers_rewards',
          trackName: 'Offers & Rewards',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LOTTERY.SYNDICATES,
          labelKey: 'lottoland_syndicates',
          trackName: 'Syndicates',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.LOTTERY.MAGAZINE,
          labelKey: 'magazine',
          trackName: 'Magazine',
        },
      ],
    },
    {
      titleKey: 'information',
      trackName: 'Information',
      links: [
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.PAYOUT,
          labelKey: 'guaranteed_payout',
          trackName: 'Guaranteed Payout',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.PRIVACY,
          labelKey: 'privacy_policy',
          trackName: 'Privacy Policy',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.TERMS,
          labelKey: 'terms_and_conditions',
          trackName: 'Terms and Conditions',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.TERMS_SUMMARY,
          labelKey: 'material_terms_summary',
          trackName: 'Material Terms',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.CORPORATE,
          labelKey: 'lottoland_corporate',
          trackName: 'Lottoland Corporate',
          target: '_blank',
          rel: 'noopener',
        },
        {
          href: ROUTES.HELP_CENTRE.BASE_URL,
          labelKey: 'help_centre',
          trackName: 'Help Centre',
          target: '_blank',
          rel: 'noopener',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.COOKIE_POLICY,
          labelKey: 'cookie_policy',
          trackName: 'Cookie Policy',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.GAME_FAIRNESS,
          labelKey: 'game_fairness',
          trackName: 'Game Fairness',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.LOTTO_BETTING,
          labelKey: 'what_is_lottobetting',
          trackName: 'What is lotto betting?',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.COMPLAINTS_PROCEDURE,
          labelKey: 'complaints_procedure',
          trackName: 'Complaints Procedure',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.INFORMATION.ACCESSIBILITY_STATEMENT,
          labelKey: 'accessibility_statement',
          trackName: 'Accessibility Statement',
        },
      ],
    },
    {
      titleKey: 'contact',
      trackName: 'Contact',
      links: [
        {
          href: ROUTES.FOOTER.NAVIGATION.CONTACT.ABOUT_US,
          labelKey: 'about_us',
          trackName: 'About us',
          target: '_blank',
          rel: 'noopener',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.CONTACT.CAREER,
          labelKey: 'career',
          trackName: 'Careers',
          target: '_blank',
          rel: 'noopener',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.CONTACT.AFFILIATE_PROGRAM,
          labelKey: 'affiliate_program',
          trackName: 'Affiliate program',
          target: '_blank',
          rel: 'noopener',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.CONTACT.B2B,
          labelKey: 'lottoland_b2b',
          trackName: 'Lottoland B2B',
          target: '_blank',
          rel: 'noopener',
        },
      ],
    },
    {
      titleKey: 'gamble_safely',
      trackName: 'Gamble Safely',
      links: [
        {
          href: ROUTES.FOOTER.NAVIGATION.GAMBLE_SAFELY.GAMBLE_SAFELY,
          labelKey: 'safer_gambling_tools',
          trackName: 'Safer Gambling Tools',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.GAMBLE_SAFELY.SUPPORT_NETWORKS,
          labelKey: 'support_networks',
          trackName: 'Support Networks',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.GAMBLE_SAFELY.UNDERAGE_PROTECTION,
          labelKey: 'underage_protection',
          trackName: 'Underage Protection',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.GAMBLE_SAFELY.DOCUMENTS,
          labelKey: 'documents_what_and_when',
          trackName: 'Documents - what and when',
        },
        {
          href: ROUTES.FOOTER.NAVIGATION.GAMBLE_SAFELY.SET_DEPOSIT_LIMIT,
          labelKey: 'set_a_deposit_limit',
          trackName: 'Deposit Limit Set',
        },
      ],
    },
  ],
};

const additionalInformation: FooterAdditionalInformationSectionConfig = {
  groups: [
    {
      titleKey: 'download_the_app',
      trackName: 'App Stores',
      items: [
        {
          imageKey: 'APP_STORE',
          alt: 'App Store',
          href: ROUTES.FOOTER.DOWNLOAD_THE_APP.APP_STORE,
          trackName: 'Apple',
          width: 120,
          height: 40,
          className: 'w-36 h-20 mr-4',
        },
        {
          imageKey: 'GOOGLE_PLAY',
          alt: 'Google Play',
          href: ROUTES.FOOTER.DOWNLOAD_THE_APP.GOOGLE_PLAY,
          trackName: 'Android',
          width: 135,
          height: 40,
          className: 'w-36 h-20',
        },
      ],
    },
    {
      titleKey: 'payment_methods',
      trackName: 'Payment Methods',
      items: [
        { imageKey: 'VISA', alt: 'Visa', width: 80, height: 80, className: 'w-20 h-20' },
        { imageKey: 'MASTERCARD', alt: 'Mastercard', width: 96, height: 66, className: 'w-20 h-20' },
        { imageKey: 'MUCH_BETTER', alt: 'Much Better', width: 80, height: 80, className: 'w-20 h-20' },
        { imageKey: 'TRUSTLY', alt: 'Trustly', width: 80, height: 80, className: 'w-20 h-20' },
        { imageKey: 'APPLE_PAY', alt: 'Apple Pay', width: 80, height: 80, className: 'w-20 h-20' },
        { imageKey: 'PAY_BY_BANK', alt: 'Pay by Bank', width: 80, height: 80, className: 'w-20 h-20' },
      ],
    },
    {
      titleKey: 'achievements',
      trackName: 'Achievements',
      items: [
        {
          imageKey: 'CLIMATE_PLEDGE',
          alt: 'The Climate Pledge',
          href: ROUTES.FOOTER.ACHIEVEMENTS.THE_CLIMATE_PLEDGE,
          trackName: 'The Climate Pledge',
          width: 80,
          height: 80,
          className: 'w-20 h-20',
        },
        {
          imageKey: 'EGR_POWER_50',
          alt: 'EGR Power 50',
          width: 80,
          height: 80,
          className: 'w-20 h-20',
        },
      ],
    },
  ],
};

const underageSection: FooterUnderageSectionConfig = {
  paragraphs: [
    {
      translationKey: 'legal_text',
      richTextLinks: [
        {
          placeholder: 'gamblingCommissionAccount',
          href: ROUTES.FOOTER.LEGAL.GAMBLING_COMMISSION_ACCOUNT,
          trackName: 'Gambling Commission',
        },
      ],
    },
    {
      translationKey: 'lottery_clarification',
    },
    {
      translationKey: 'jackpots_clarification',
      richTextLinks: [
        {
          placeholder: 'terms',
          href: ROUTES.FOOTER.LEGAL.TERMS,
          trackName: 'Terms and Conditions',
        },
      ],
    },
    {
      translationKey: 'gambling_awareness',
      richTextLinks: [
        {
          placeholder: 'saferGambling',
          href: ROUTES.FOOTER.LEGAL.BEGAMBLEAWARE,
          trackName: 'Gamble Aware',
        },
        {
          placeholder: 'gambleSafely',
          href: ROUTES.FOOTER.LEGAL.GAMBLE_SAFELY,
          trackName: 'Gamble Safely',
        },
        {
          placeholder: 'setDepositLimit',
          href: ROUTES.FOOTER.LEGAL.SET_DEPOSIT_LIMIT,
          trackName: 'Set Deposit Limit',
        },
      ],
    },
  ],
  imageSection: {
    leadImageTextKey: 'underage_gambling',
    leadImage: {
      imageKey: 'UNDERAGE_GAMBLING',
      alt: 'Underage gambling',
      width: 32,
      height: 32,
    },
    items: [
      {
        imageKey: 'GAMBLE_SAFELY',
        alt: 'Gamble Safely',
        href: ROUTES.FOOTER.LEGAL.GAMBLE_SAFELY,
        trackName: 'Gamble Safely',
        width: 150,
        height: 90,
      },
      {
        imageKey: 'GAMBLE_AWARE',
        alt: 'Gamble Aware',
        href: ROUTES.FOOTER.LEGAL.GAMBLE_AWARE,
        trackName: 'Gamble Aware',
        width: 160,
        height: 160,
      },
      {
        imageKey: 'GAMBLING_COMMISSION',
        alt: 'Gambling Commission',
        href: ROUTES.FOOTER.LEGAL.GAMBLING_COMMISSION,
        trackName: 'Gambling Commission',
        width: 160,
        height: 160,
      },
      {
        imageKey: 'GAMSTOP',
        alt: 'GAMSTOP',
        href: ROUTES.FOOTER.LEGAL.GAMSTOP,
        trackName: 'GamStop',
        width: 160,
        height: 160,
      },
      {
        imageKey: 'NATIONAL_GAMBLING_HELPLINE',
        alt: 'National Gambling Helpline',
        href: ROUTES.FOOTER.LEGAL.GAMCARE,
        trackName: 'National Gambling Helpline',
        width: 160,
        height: 160,
      },
      {
        imageKey: 'TAKE_TIME_TO_THINK',
        alt: 'Take Time to Think',
        href: ROUTES.FOOTER.LEGAL.TAKE_TIME_TO_THINK,
        trackName: 'Take Time to Think',
        width: 160,
        height: 160,
      },
    ],
  },
};

const copyrightAndSocials: FooterCopyrightAndSocialsSectionConfig = {
  copyrightTranslationKey: 'copyright',
  socialLinks: [
    {
      href: ROUTES.FOOTER.SOCIALS.INSTAGRAM,
      label: 'Instagram',
      iconKey: 'Instagram',
      trackName: 'Instagram',
    },
    {
      href: ROUTES.FOOTER.SOCIALS.FACEBOOK,
      label: 'Facebook',
      iconKey: 'Facebook',
      trackName: 'Facebook',
    },
  ],
};

const footerConfig: FooterConfig = { contactSection, additionalInformation, copyrightAndSocials, navigationLinks, underageSection };

export { footerConfig };
